import {
    RESERVATION_SCOPE,
    RESERVATION_STATUS,
    RESERVATION_TYPE,
} from 'api/reservations/constants';
import {AxiosRequestConfig} from 'axios';
import {spotheroPost} from 'api';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {ApiResponse} from 'api/api.types';
import {
    FormattedReservationDailyRequest,
    GetReservationDailyParams,
    ReservationsDailyResponse,
} from './reservations.types';

/**
 * Formats the response from the get reservation daily endpoint so that it can be consumed by the UI
 * @param data - The response from the get reservation endpoint
 * @return The formatted response
 *
 * @memberof module:api/reservations/dto
 */
export const formatGetReservationsDailyResponse = (
    data: ApiResponse<ReservationsDailyResponse>
): ReservationsDailyResponse => {
    const formattedData: ReservationsDailyResponse = camelizeKeys(data);

    return formattedData;
};

/**
 * Formats the request for the get reservation daily endpoint
 * @param request - The request to the get reservation endpoint
 * @return The formatted request
 *
 * @memberof module:api/reservations/dto
 */
export const formatGetReservationsDailyRequest = (
    request: GetReservationDailyParams
): FormattedReservationDailyRequest => {
    const updatedRequest = {
        filters: {
            reservationTypes:
                request.reservationTypes !== RESERVATION_TYPE.all
                    ? request.reservationTypes
                    : '',
            statuses:
                request.statuses !== RESERVATION_STATUS.all
                    ? request.statuses
                    : '',
            scope: request.scope !== RESERVATION_SCOPE.all ? request.scope : '',
        },
        searchText: request.searchText,
        limit: 25,
        offset: request.offset,
    };

    const formattedRequest: FormattedReservationDailyRequest =
        convertKeysToSnakeCase(updatedRequest);

    return formattedRequest;
};

/**
 * Gets an operator's daily reservations.
 *
 * @param requestData - The data required to fetch daily reservations.
 * @param requestData.facilityId - The ID of the facility for which reservations are being fetched.
 * @param requestData.reservationTypes - The types of reservations to include, defaults to 'all'
 * @param requestData.scope - The scope of reservations to include, defaults to 'all'
 * @param requestData.statuses - The statuses of reservations to filter by, defaults to 'all'
 * @param requestData.searchText - Text to filter reservations by, such as customer name or reservation ID.
 * @param requestData.offset - The pagination offset for the reservations, defaults to 0
 * @param config - Additional Axios configuration options for the request.
 * @param config.signal - An abort signal to cancel the request.
 * @returns A promise that resolves to the formatted daily reservations data.
 */
const getReservationsDaily = async (
    requestData: GetReservationDailyParams,
    config: AxiosRequestConfig = {}
): Promise<ReservationsDailyResponse> => {
    try {
        const formattedRequest = formatGetReservationsDailyRequest(requestData);

        const response = await spotheroPost<ReservationsDailyResponse>(
            `operator-reservations/facilities/${requestData.facilityId}/daily/`,
            formattedRequest,
            config,
            false
        );

        const formattedResponse = formatGetReservationsDailyResponse(response);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw new Error(error.message);
    }
};

export default getReservationsDaily;
